import React, { useMemo } from "react";

/**
 * Feature 9 - Dynamic Watermark.
 *
 * A transparent, non-selectable, repeated diagonal overlay embedding
 * user-specific info (username, email, timestamp, session, optional IP /
 * document name) to discourage screenshots and leaked copies.
 *
 * This is an ADDITIVE overlay: it renders above the document view without
 * touching the existing steganography/floating-text watermark in DocumentRPD.
 * Renders nothing unless `enabled` is true.
 */
export function Watermark({
  enabled,
  email = "",
  username = "",
  sessionId = "",
  ipAddress = "",
  documentName = "",
  opacity = 0.12,
}) {
  const lines = useMemo(() => {
    const now = new Date();
    const stamp =
      now.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      " " +
      now.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });

    const parts = [];
    if (username) parts.push(username);
    if (email) parts.push(email);
    parts.push(stamp);
    if (sessionId) parts.push("Session: " + sessionId);
    if (ipAddress) parts.push("IP: " + ipAddress);
    if (documentName) parts.push(documentName);
    return parts;
  }, [email, username, sessionId, ipAddress, documentName]);

  if (!enabled) return null;

  const label = lines.join("   •   ");

  // Build a repeated tile grid so the pattern covers the whole surface and
  // stays responsive to zoom/resize (parent is position:relative).
  const cells = [];
  for (let i = 0; i < 60; i += 1) {
    cells.push(
      <div key={i} className="dp-watermark-cell">
        {label}
      </div>
    );
  }

  return (
    <div
      className="dp-watermark-overlay"
      aria-hidden="true"
      style={{ opacity }}
    >
      <div className="dp-watermark-grid">{cells}</div>
    </div>
  );
}

export default Watermark;
